import { createClient } from "@supabase/supabase-js";
import { Database } from "../database.types";

const rawUrl = import.meta.env.VITE_SUPABASE_URL;
const rawKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const rawProjectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;

// Only log configuration status, never log actual keys
//console.log("🔍 DB URL configured:", !!rawUrl);
//console.log("🔍 Publishable key configured:", !!rawKey);

// Clean up URLs by removing any line breaks or whitespace
const supabaseUrl = rawUrl?.replace(/\s+/g, "").trim();
const supabasePublishableKey = rawKey?.replace(/\s+/g, "").trim();
let supabaseProjectId = rawProjectId?.replace(/\s+/g, "").trim();
// Derive project ref from URL if not provided
if (!supabaseProjectId && typeof supabaseUrl === "string") {
	try {
		const u = new URL(supabaseUrl);
		const host = u.host || "";
		const ref = host.split(".")[0];
		if (ref && ref !== "localhost") {
			supabaseProjectId = ref;
		}
	} catch {}
}

//console.log("Supabase URL:", supabaseUrl);
//console.log("Supabase publishable key configured:", !!supabasePublishableKey);

// Utility to clear any stored Supabase auth/session keys
export function clearSupabaseAuthStorage() {
	if (typeof window === "undefined") return;
	try {
		const ls = window.localStorage;
		const keys = Object.keys(ls);
		for (const k of keys) {
			if (k.startsWith("sb-")) {
				ls.removeItem(k);
			}
		}
		// Clear potential PKCE helpers
		const ss = window.sessionStorage;
		if (ss) {
			Object.keys(ss).forEach((k) => {
				if (k.startsWith("sb-")) ss.removeItem(k);
			});
		}
	} catch {}
}

// If the connected Supabase project changes between runs, clear stale tokens
if (typeof window !== "undefined") {
	const STORAGE_KEY = "app.supabase.projectId";
	const prev = window.localStorage.getItem(STORAGE_KEY);
	if (supabaseProjectId && prev && prev !== supabaseProjectId) {
		console.warn("Supabase project changed, clearing stale auth/session state");
		clearSupabaseAuthStorage();
	}
	if (supabaseProjectId) {
		window.localStorage.setItem(STORAGE_KEY, supabaseProjectId);
	}
}

// Proactively sanitize stale/invalid auth storage BEFORE client init to avoid pre-login refresh attempts
if (typeof window !== "undefined") {
	try {
		const ls = window.localStorage;
		const keys = Object.keys(ls);
		for (const k of keys) {
			// Only inspect auth-token keys written by supabase-js v2
			if (k.startsWith("sb-") && k.endsWith("-auth-token")) {
				try {
					const raw = ls.getItem(k);
					if (!raw) continue;
					const parsed = JSON.parse(raw);
					const cs = parsed?.currentSession;
					const rt = cs?.refresh_token || cs?.refreshToken; // support both shapes
					const exp = Number(cs?.expires_at || cs?.expiresAt || 0);
					const now = Math.floor(Date.now() / 1000);

					// Remove obviously invalid or expired-without-refresh tokens
					// Reduced buffer from 60s to 10s to avoid removing valid tokens that are about to refresh
					if (
						!rt ||
						typeof rt !== "string" ||
						rt.length < 20 ||
						(exp && exp < now - 10)
					) {
						ls.removeItem(k);
					}
				} catch {
					// If unreadable, remove the bad entry
					ls.removeItem(k);
				}
			}
		}
	} catch {}
}

// Graceful handling of missing environment variables
let finalUrl = supabaseUrl;
let finalKey = supabasePublishableKey;

if (!supabaseUrl || !supabasePublishableKey) {
	console.warn("Missing Supabase environment variables:", {
		VITE_SUPABASE_URL: !!rawUrl,
		VITE_SUPABASE_PUBLISHABLE_KEY: !!rawKey,
	});

	throw new Error(
		"Supabase URL and publishable key are required. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY environment variables.",
	);
} else {
	// Validate that we're using a publishable key, not a secret key
	if (supabasePublishableKey?.startsWith("sb_secret_")) {
		throw new Error(
			"Security Error: Cannot use secret API key in browser. Please use the publishable key instead.",
		);
	}

	if (!supabasePublishableKey?.startsWith("sb_publishable_")) {
		throw new Error(
			"Invalid Supabase key format. Please use the new publishable key format that starts with 'sb_publishable_'.",
		);
	}

	// Validate the URL format
	try {
		new URL(supabaseUrl);
	} catch (error) {
		throw new Error(`Invalid Supabase URL format: ${supabaseUrl}`);
	}
}

export const supabase = createClient<Database>(finalUrl, finalKey, {
	auth: {
		persistSession: true,
		autoRefreshToken: true,
		detectSessionInUrl: true,
		flowType: "pkce",
		storageKey: supabaseProjectId ? `rj-${supabaseProjectId}-auth` : undefined,
	},
});

// Archiving utility functions
export const archiveReflection = async (
	reflectionId: string,
	userId?: string,
) => {
	if (!userId) {
		return {
			data: null,
			error: new Error("User ID required to archive reflection"),
		};
	}

	const { data, error } = await supabase
		.from("reflections")
		.update({ archived: true })
		.eq("id", reflectionId)
		.eq("user_id", userId)
		.select();

	return { data, error };
};

export const unarchiveReflection = async (
	reflectionId: string,
	userId?: string,
) => {
	if (!userId) {
		return {
			data: null,
			error: new Error("User ID required to unarchive reflection"),
		};
	}

	const { data, error } = await supabase
		.from("reflections")
		.update({ archived: false })
		.eq("id", reflectionId)
		.eq("user_id", userId)
		.select();

	return { data, error };
};

export type Database = {
	public: {
		Tables: {
			profiles: {
				Row: {
					id: string;
					email: string;
					name: string;
					user_type: "mentee" | "mentor";
					created_at: string;
					updated_at: string;
				};
				Insert: {
					id: string;
					email: string;
					name: string;
					user_type: "mentee" | "mentor";
					created_at?: string;
					updated_at?: string;
				};
				Update: {
					id?: string;
					email?: string;
					name?: string;
					user_type?: "mentee" | "mentor";
					created_at?: string;
					updated_at?: string;
				};
			};
			reflections: {
				Row: {
					id: string;
					user_id: string;
					title: string;
					description: string;
					current_state: string;
					future_state: string;
					qa_results: any;
					topic_tags: string[];
					created_at: string;
					updated_at: string;
					is_draft?: boolean;
					archived: boolean;
					archived_at: string | null;
					archived_by: string | null;
				};
				Insert: {
					id?: string;
					user_id: string;
					title: string;
					description: string;
					current_state?: string;
					future_state?: string;
					qa_results?: any;
					topic_tags?: string[];
					created_at?: string;
					updated_at?: string;
					is_draft?: boolean;
					archived?: boolean;
					archived_at?: string | null;
					archived_by?: string | null;
				};
				Update: {
					id?: string;
					user_id?: string;
					title?: string;
					description?: string;
					current_state?: string;
					future_state?: string;
					qa_results?: any;
					topic_tags?: string[];
					created_at?: string;
					updated_at?: string;
					is_draft?: boolean;
					archived?: boolean;
					archived_at?: string | null;
					archived_by?: string | null;
				};
			};
			contributing_factors: {
				Row: {
					id: string;
					reflection_id: string;
					title: string;
					description: string;
					created_at: string;
				};
				Insert: {
					id?: string;
					reflection_id: string;
					title: string;
					description: string;
					created_at?: string;
				};
				Update: {
					id?: string;
					reflection_id?: string;
					title?: string;
					description?: string;
					created_at?: string;
				};
			};
			signals: {
				Row: {
					id: string;
					reflection_id: string;
					title: string;
					description: string;
					created_at: string;
				};
				Insert: {
					id?: string;
					reflection_id: string;
					title: string;
					description: string;
					created_at?: string;
				};
				Update: {
					id?: string;
					reflection_id?: string;
					title?: string;
					description?: string;
					created_at?: string;
				};
			};
			corrective_actions: {
				Row: {
					id: string;
					reflection_id: string;
					title: string;
					description: string;
					priority: number;
					status: "not_started" | "in_progress" | "completed";
					created_at: string;
					updated_at: string;
				};
				Insert: {
					id?: string;
					reflection_id: string;
					title: string;
					description: string;
					priority?: number;
					status?: "not_started" | "in_progress" | "completed";
					created_at?: string;
					updated_at?: string;
				};
				Update: {
					id?: string;
					reflection_id?: string;
					title?: string;
					description?: string;
					priority?: number;
					status?: "not_started" | "in_progress" | "completed";
					created_at?: string;
					updated_at?: string;
				};
			};
			daily_checkin_analysis: {
				Row: {
					id: string;
					user_id: string;
					analysis_date: string;
					checkin_count: number;
					last_checkin_date: string;
					analysis_data: any;
					created_at: string;
					updated_at: string;
				};
				Insert: {
					id?: string;
					user_id: string;
					analysis_date?: string;
					checkin_count: number;
					last_checkin_date: string;
					analysis_data: any;
					created_at?: string;
					updated_at?: string;
				};
				Update: {
					id?: string;
					user_id?: string;
					analysis_date?: string;
					checkin_count?: number;
					last_checkin_date?: string;
					analysis_data?: any;
					created_at?: string;
					updated_at?: string;
				};
			};
			daily_checkins: {
				Row: {
					id: string;
					user_id: string;
					reflection_id: string | null;
					general_feeling: number;
					day_description: string;
					reflection_feeling: number | null;
					reflection_impact: string | null;
					reflection_reasons: string | null;
					signals_noticed: string[];
					corrective_actions_taken: string[];
					created_at: string;
				};
				Insert: {
					id?: string;
					user_id: string;
					reflection_id?: string | null;
					general_feeling: number;
					day_description: string;
					reflection_feeling?: number | null;
					reflection_impact?: string | null;
					reflection_reasons?: string | null;
					signals_noticed?: string[];
					corrective_actions_taken?: string[];
					created_at?: string;
				};
				Update: {
					id?: string;
					user_id?: string;
					reflection_id?: string | null;
					general_feeling?: number;
					day_description?: string;
					reflection_feeling?: number | null;
					reflection_impact?: string | null;
					reflection_reasons?: string | null;
					signals_noticed?: string[];
					corrective_actions_taken?: string[];
					created_at?: string;
				};
			};
			reflection_analysis: {
				Row: {
					id: string;
					user_id: string;
					reflection_id: string;
					analysis_date: string;
					checkin_count: number;
					last_checkin_date: string | null;
					analysis_data: {
						positiveObservations: Array<{
							observation: string;
							frequency: number;
							impact: string;
							signals: string[];
							context: string;
						}>;
						negativeObservations: Array<{
							observation: string;
							frequency: number;
							impact: string;
							signals: string[];
							context: string;
						}>;
						impactTrends: {
							overall: "improving" | "declining" | "stable";
							recentTrend: string;
							significantChanges: string[];
						};
						signalPatterns: Array<{
							signal: string;
							frequency: number;
							correlation: string;
							impact: string;
						}>;
						insights: {
							keyFindings: string[];
							progressIndicators: string[];
							concernAreas: string[];
						};
						recommendations: Array<{
							action: string;
							priority: "high" | "medium" | "low";
							reasoning: string;
							timeframe: string;
						}>;
					};
					created_at: string;
					updated_at: string;
				};
				Insert: {
					id?: string;
					user_id: string;
					reflection_id: string;
					analysis_date?: string;
					checkin_count?: number;
					last_checkin_date?: string | null;
					analysis_data: any;
					created_at?: string;
					updated_at?: string;
				};
				Update: {
					id?: string;
					user_id?: string;
					reflection_id?: string;
					analysis_date?: string;
					checkin_count?: number;
					last_checkin_date?: string | null;
					analysis_data?: any;
					created_at?: string;
					updated_at?: string;
				};
			};
			ai_prompts: {
				Row: {
					id: string;
					type:
						| "overall_context"
						| "reflection_qa"
						| "contributing_factors"
						| "signals"
						| "corrective_actions"
						| "future_state"
						| "reflection_analysis";
					prompt: string;
					created_at: string;
					updated_at: string;
				};
				Insert: {
					id?: string;
					type:
						| "overall_context"
						| "reflection_qa"
						| "contributing_factors"
						| "signals"
						| "corrective_actions"
						| "future_state"
						| "reflection_analysis";
					prompt: string;
					created_at?: string;
					updated_at?: string;
				};
				Update: {
					id?: string;
					type?:
						| "overall_context"
						| "reflection_qa"
						| "contributing_factors"
						| "signals"
						| "corrective_actions"
						| "future_state"
						| "reflection_analysis";
					prompt?: string;
					created_at?: string;
					updated_at?: string;
				};
			};
			group_analysis: {
				Row: {
					id: string;
					mentor_id: string;
					group_id: string;
					group_name: string;
					analysis_date: string;
					member_count: number;
					checkin_count: number;
					reflection_count: number;
					analysis_data: any;
					created_at: string;
					updated_at: string;
				};
				Insert: {
					id?: string;
					mentor_id: string;
					group_id: string;
					group_name: string;
					analysis_date?: string;
					member_count?: number;
					checkin_count?: number;
					reflection_count?: number;
					analysis_data: any;
					created_at?: string;
					updated_at?: string;
				};
				Update: {
					id?: string;
					mentor_id?: string;
					group_id?: string;
					group_name?: string;
					analysis_date?: string;
					member_count?: number;
					checkin_count?: number;
					reflection_count?: number;
					analysis_data?: any;
					created_at?: string;
					updated_at?: string;
				};
			};
		};
	};
};
